/**
 * SalesDealDetail.tsx — Sales / per-deal detail page.
 *
 * Deal detail slice 1 (design note §39.9, order 1 — "layout and copy, no new
 * data"; spec §3.1–§3.3, §3.6 loading, §3.0 / §4 copy): the hero (account
 * tile, priority pill, meta line, the value in short form with the exact
 * figure in its tooltip, the ⋯ menu per R7), the stage bar with "Move to …"
 * and a 5-second Undo (a reverse move — the events table is append-only),
 * skeletons, and every developer string the §39.2 inventory found mapped to a
 * sentence. The breadcrumb and the "Deal N of M" pager live in the shell
 * (Dashboard passes the third crumb and the pager); J / K step the board's
 * order from here. The "← Back to …" link remains ONLY for the openers that
 * have no crumb for the deal (Home, Inbound leads): under Pipeline the
 * breadcrumb does that job (spec §3.1).
 *
 * Earlier history (Deal Detail v1): docs/design/sales-build-design.md §3,
 * decisions D3-1a (tasks via sales_activities type 'task'), D3-2 (timeline =
 * manual entries + synthesised entries), D3-4 (Emails omitted — no backing),
 * D3-5 superseded by slice 3a (§5.E): the close command is live, so the hero
 * carries Mark won / Mark lost into the shared CloseDealModal; the won deal's
 * minted draft renders as an Invoice row.
 *
 * The deal is fetched FRESH by id (never rendered from a stale card copy);
 * mutation responses land in a keyed override so no fetched-state is copied
 * into local state (no setState-in-effect).
 *
 * Slice 4 (§39.9 order 4; spec §3.4 B): the Next steps card replaces the
 * Tasks card — NextStepsCard.tsx, rules in nextSteps.ts, working days in
 * workingDays.ts (R1).
 *
 * Slice 6 (§39.9 order 6; spec §3.4 A, §3.2, §3.3): the ✦ Deal health card
 * (DealHealthCard.tsx, rules in dealHealthCard.ts) heads the left column on an
 * open deal; its summary reads slice 5's pipeline-position route. The stage
 * bar's time in stage is London calendar days from the server's clock and
 * reads red past R6's threshold (2 × the stage's won-deal median from the
 * stage-medians route, else 21 days) — a colour, no notification. The hero
 * shows "Weighted £{x} · {n}%" beside the value. The orange server-signal
 * chips beside the stage bar are gone from this page: their facts live in the
 * stage bar (time in stage) and the health card (activity, close date).
 *
 * Slice 8 (§39.9 order 8; spec §3.5 A, B, §3.6): People on this deal reads
 * the deal's LINKS (0133's GET /contacts) — DealPeopleCard.tsx, with Link from
 * slice 5's suggestions, Set primary, Unlink and the single-threaded warning;
 * the health card's Contact check counts those links and the "Send intro
 * email" chip names the PRIMARY; the ✦ Close date check (CloseDateCard.tsx,
 * rules in closeDateCheck.ts, Keep per R2); the timeline names link, unlink,
 * primary and Keep entries and who logged each activity; the hero and the
 * Forecast row show the SERVER's weighted value (rows 216, 217).
 *
 * NOT built yet, by ruling (§39.9): the deep link (9).
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useCallback, useEffect, useState, useRef } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Avatar, ErrorState } from '../../DashboardShared'
import type { DealDetail, DealStage } from '../../types'
import type { SalesActivity as WireActivity, SalesDealStageEvent, SalesFieldHistoryEntry } from '../../../../api/types'
import { recordRiskFlagAction, fetchDealRiskFlags,
  useAsync, fetchDeal, fetchContact, fetchDealActivities, fetchDealStageEvents, fetchFieldHistory, fetchDirectory, fetchAccounts,
  createDealActivity, completeActivity, reopenActivity,
  moveDealStage, deleteDeal, mapDealDetail, cachedMeIdentity,
  fetchDealPipelinePosition, fetchDealContacts,
  STAGE_TO_SLUG,
} from '../../api'
import { CloseDealModal } from './CloseDealModal'
import { DealTimeline } from './DealTimeline'
import { NextStepsCard } from './NextStepsCard'
import { isTaskDone } from './nextSteps'
import { ForecastCard, DetailsCard } from './DealRail'
import { DealPeopleCard } from './DealPeopleCard'
import { CloseDateCard } from './CloseDateCard'
import { closeDateCard } from './closeDateCheck'
import type { SalesDealContacts } from '../../../../api/types'
import { formatDay, sourcePhrase, weightedLabel } from './dealRail'
import { buildTimeline } from './dealTimeline'
import { useLogActivity } from './logActivityShared'
import type { CloseOutcome } from './closeFlow'
import { DealHealthCard } from './DealHealthCard'
import { healthChecks, summarySentence } from './dealHealthCard'
import { londonDay } from './workingDays'
import { serverNow } from '../../../../lib/serverClock'
import { isClosedStage } from './dealEdit'
import { DealMenu } from './DealMenu'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { StageBar } from './StageBar'
import { initialsOf, priorityPill } from './dealCopy'
import { dealPosition, DEAL_NEXT_KEY, DEAL_PREV_KEY } from './dealNav'
import { shortcutEligible } from '../../../../lib/keyboardGuard'
import { shortcutsPrefKey, readShortcutsEnabled } from '../../../../lib/shortcutsPref'
import { ApiError, apiErrorMessage } from '../../../../api/client'
import type { RiskFlagActionBody } from '../../../../api/types'
import { DealNotFound } from './DealNotFound'
import { toast } from 'sonner'
import { info, ink, success, surface, warning } from '../../../../theme/tokens'
import { money, compactMoney, fxProvenance } from '../../../../lib/money'
import { refreshSalesFigures } from '../../salesFigures'

// The stages a move may land IN (decision #7: closed stages only via the close
// command — the hero's Mark won / Mark lost, CloseDealModal).
const OPEN_STAGES: DealStage[] = ['New Lead', 'Qualified', 'Proposal Sent', 'Negotiation']


/** The toast's Undo window (spec §3.6: "Undo is available for 5 seconds"). */
export const UNDO_MS = 5000

// ── Loading (spec §3.6: skeletons for the hero, the stage bar and each card;
//    no spinner) ──────────────────────────────────────────────────────────────

export function DealDetailSkeleton() {
  return (
    <div className="page-enter flex flex-col gap-5" aria-busy="true" aria-label="Loading deal" data-deal-skeleton>
      <div className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="k-skeleton w-14 h-14" />
          <div className="flex flex-col gap-2 pt-1">
            <div className="k-skeleton h-7 w-64" />
            <div className="k-skeleton h-4 w-80" />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="k-skeleton h-9 w-28" />
          <div className="k-skeleton h-9 w-56" />
        </div>
      </div>
      <div className="k-card px-5 py-4">
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
          {[0, 1, 2, 3, 4].map(i => <div key={i}><div className="k-skeleton h-1.5" /><div className="k-skeleton h-4 w-20 mt-2" /></div>)}
        </div>
      </div>
      <div className="grid grid-cols-1 min-[1100px]:grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
        <div className="flex flex-col gap-5 min-w-0">
          {[0, 1].map(i => <div key={i} className="k-card p-5"><div className="k-skeleton h-4 w-32 mb-4" /><div className="k-skeleton h-24" /></div>)}
        </div>
        <div className="flex flex-col gap-5 min-w-0">
          {[0, 1, 2].map(i => <div key={i} className="k-card p-5"><div className="k-skeleton h-4 w-32 mb-4" /><div className="k-skeleton h-40" /></div>)}
        </div>
      </div>
    </div>
  )
}

// ── Timeline entry shape (manual wire rows + synthesised rows) ───────────────

/** History and stage events, re-read SILENTLY after every write so each move
 *  lands as its own timeline entry (slice 2) without the card flashing back to
 *  its skeleton. The first read for a deal shows the skeleton; later reads
 *  keep the last good lists until the new ones land. */
function useDealChanges(dealId: string) {
  const [state, setState] = useState<{
    id: string; history: SalesFieldHistoryEntry[] | null; events: SalesDealStageEvent[] | null
    historyError: boolean; eventsError: boolean
  }>({ id: '', history: null, events: null, historyError: false, eventsError: false })
  const [rev, setRev] = useState(0)
  useEffect(() => {
    let alive = true
    Promise.allSettled([fetchFieldHistory('deals', dealId), fetchDealStageEvents(dealId)]).then(([h, ev]) => {
      if (!alive) return
      setState(prev => {
        const same = prev.id === dealId
        return {
          id: dealId,
          history: h.status === 'fulfilled' ? h.value : (same ? prev.history : null),
          events: ev.status === 'fulfilled' ? ev.value : (same ? prev.events : null),
          historyError: h.status === 'rejected' && !(same && prev.history),
          eventsError: ev.status === 'rejected' && !(same && prev.events),
        }
      })
    })
    return () => { alive = false }
  }, [dealId, rev])
  const refresh = useCallback(() => setRev(r => r + 1), [])
  const current = state.id === dealId
  return {
    history: current ? state.history : null,
    events: current ? state.events : null,
    loading: !current,
    historyError: current && state.historyError,
    eventsError: current && state.eventsError,
    refresh,
  }
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function SalesDealDetail({ dealId, onBack, backLabel, onNavigate, onOpenAccount, canDelete = false, riskFlagsOn = false, order = null, onNavigateDeal, onHeader, onDeleted, onNotFound }: {
  dealId: string
  /** The openers with no crumb for the deal (Home, Inbound leads) keep a
   *  Back link; under Pipeline the shell's breadcrumb is the way back and
   *  neither is passed. */
  onBack?: () => void
  backLabel?: string
  onNavigate?: (dest: string) => void
  /** Slice A: opens the account 360 for a deal that carries an account_id. */
  onOpenAccount?: (accountId: string) => void
  /** R7 / Rider A: the session role is one the server's destructive gate
   *  allows (owner or admin). Fail closed: absent = no Delete item. */
  canDelete?: boolean
  /** R3: /v1/me lists the tenant's sales.risk_flags switch as on. Fail closed. */
  riskFlagsOn?: boolean
  /** The board's display order the deal was opened from (null = no stepping). */
  order?: readonly string[] | null
  onNavigateDeal?: (id: string) => void
  /** The shell's third crumb and pager read the title and stage from here. */
  onHeader?: (h: { title: string; stage: DealStage }) => void
  /** After a confirmed delete: the shell returns to the list. */
  onDeleted?: () => void
  /** Slice 9: the address named no deal here (the shell's crumb says so). */
  onNotFound?: (dealId: string) => void
}) {
  // Slice 9 (R9): a 404 — an unknown id, or another tenant's, which the server
  // answers identically — is NO deal (null), not an error: it reads "Deal not
  // found", never the server's text. Any other failure stays an error.
  const dealQ = useAsync(() => fetchDeal(dealId).catch((e: unknown) => {
    if (e instanceof ApiError && e.status === 404) return null
    throw e
  }), [dealId])
  const actsQ = useAsync(() => fetchDealActivities(dealId), [dealId])
  // The stage bar's past-step times and the timeline's "after N days" come
  // from the EXISTING stage-events read (no new data, §39.9 order 1); the
  // audit strip below reads it again for its own list (B-1 keeps the reads
  // separate on the wire; sharing one fetch is slice 2's merge).
  const changes = useDealChanges(dealId)
  // Join targets for the timeline's id-valued edits — best-effort: a failed
  // read degrades an edit to its plain generic sentence, never to a raw id.
  const employeesQ = useAsync(() => fetchDirectory(), [])
  const accountsQ = useAsync(() => fetchAccounts(), [])
  const logActivity = useLogActivity()

  // Mutation responses override the fetched deal (keyed — never copied into
  // state from the fetch itself). Activity adds/patches merge the same way.
  const [override, setOverride] = useState<{ id: string; deal: DealDetail } | null>(null)
  const [added, setAdded] = useState<WireActivity[]>([])
  const [patched, setPatched] = useState<Record<string, WireActivity>>({})
  const [moving, setMoving] = useState(false)
  // The live deal and the in-flight flag, for the one handler that outlives
  // its render: the toast's Undo (see moveTo). Written on every render, below.
  const liveDeal = useRef<DealDetail | null | undefined>(undefined)
  const inFlight = useRef(false)
  const [deleting, setDeleting] = useState(false)
  // The close ceremony (§5.E) — preset outcome, shared CloseDealModal.
  const [closing, setClosing] = useState<CloseOutcome | null>(null)
  // Every deal write on this page (edit, stage move, close, tasks, delete) is
  // records-level (lib/writeAccess; fail closed). The risk-flag act and the
  // committee roles keep their own per-deal props (mayAct, canSetRoles).
  const { records } = useWriteAccess()
  // Slice 3: the ⋯ menu's Edit opens the Details card's Title row.
  const [editTitleSignal, setEditTitleSignal] = useState(0)

  const deal = override && override.id === dealId ? override.deal : dealQ.data
  liveDeal.current = deal
  // The primary contact's name, for a "Primary contact changed to …" entry.
  const contactId = deal?.contactId
  const contactQ = useAsync(() => (contactId ? fetchContact(contactId) : Promise.resolve(null)), [contactId])
  // Slice 6: the summary's rank and share (re-read when the stage or value
  // moves). A failed read leaves its part out. R6's medians are the server's
  // now (§40.12): the stage threshold arrives on deal.assessment.
  const positionQ = useAsync(() => fetchDealPipelinePosition(dealId), [dealId, deal?.stage, deal?.amountPence])
  // Slice 8: the deal's links (0133). A link write's response replaces the
  // read (keyed, as the deal's own override); a failed read leaves the
  // health check on the primary mirror and the card says it could not load.
  const linksQ = useAsync(() => fetchDealContacts(dealId), [dealId])
  const [linksOverride, setLinksOverride] = useState<{ id: string; links: SalesDealContacts } | null>(null)
  const links = linksOverride && linksOverride.id === dealId ? linksOverride.links : linksQ.data
  // Names for contact ids the history mentions that are no longer linked (an
  // unlinked contact still has a name); a deleted one reads "A contact".
  const linkedIds = new Set((links?.items ?? []).map(i => i.contactId))
  const historyContactIds = [...new Set((changes.history ?? [])
    .filter(h => h.field === 'contact_linked' || h.field === 'contact_unlinked' || h.field === 'contact_id')
    .flatMap(h => [h.oldValue, h.newValue])
    .filter((v): v is string => Boolean(v) && !linkedIds.has(v as string)))].sort()
  const namesQ = useAsync(
    () => Promise.all(historyContactIds.map(id => fetchContact(id).then(c => [id, c.name] as const).catch(() => null))),
    [historyContactIds.join(',')],
  )
  // An activity logged through the sheet (the composer's Call / Email /
  // Meeting) lands in this deal's timeline the moment the server has it.
  // §40.12: the health card and the stage bar render the SERVER's assessment,
  // so EVERY write this page makes re-reads the deal (ruled 2026-10-17): a
  // stage move and its Undo, an inline field edit, an override set or
  // cleared, the close-date Keep (all through applyWrite), and an activity, a
  // task, a note or a contact link. A failed re-read leaves the last
  // assessment shown.
  // ORDER (ruled 2026-10-17): every server answer that REPLACES the deal — a
  // write's answer or a re-read — carries a ticket taken when its request
  // STARTED, and is applied only if no answer to a later-started request has
  // been applied already. So a slow older write, or a re-read that started
  // before a newer write, never replaces the newer state
  // (SalesDealDetail.reread.test.tsx forces both orders).
  const ticket = useRef(0)
  const appliedTicket = useRef(0)
  const applyAnswer = useCallback((t: number, id: string, d: DealDetail): boolean => {
    if (t < appliedTicket.current) return false
    appliedTicket.current = t
    setOverride({ id, deal: d })
    return true
  }, [])
  const reassess = useCallback(() => {
    const t = ++ticket.current
    const id = dealId
    fetchDeal(id).then(d => { applyAnswer(t, id, d) }).catch(() => {})
  }, [dealId, applyAnswer])
  // R3: the rule whose act is in flight (one at a time). Declared here, with
  // the page's other hooks, before any early return.
  const [acting, setActing] = useState<string | null>(null)
  // R4: whether THIS session may confirm or dismiss the deal's flags is the
  // SERVER's answer (viewerMayAct on the risk-flags read: owner/admin, or the
  // deal's linked owner whatever their role) — never a client-side role test.
  // Re-read when the deal's owner changes; no request while the switch is
  // off; a failed read shows no controls (fail closed).
  const assignee = deal?.assignedEmployeeId
  const mayActQ = useAsync(() => (riskFlagsOn ? fetchDealRiskFlags(dealId) : Promise.resolve(null)), [dealId, riskFlagsOn, assignee])
  const mayAct = mayActQ.data?.viewerMayAct === true
  useEffect(() => logActivity?.subscribe(created => {
    if (created.dealId === dealId) { setAdded(prev => [created, ...prev]); reassess() }
  }), [logActivity, dealId, reassess])

  // The shell's crumb and pager: the title and stage, whenever they change.
  const headerTitle = deal?.title
  const headerStage = deal?.stage
  useEffect(() => {
    if (headerTitle !== undefined && headerStage !== undefined) onHeader?.({ title: headerTitle, stage: headerStage })
  }, [headerTitle, headerStage, onHeader])

  // J / K (spec §3.1): one document listener for the page's life, the L
  // listener's shape (LogActivityHost): the Settings single-key switch is
  // honoured first (row 150's switch), then the bare-key guard (not typing,
  // nothing modal), then the board order decides whether there is anywhere
  // to go. The keys are named POSITIVELY (`.key ===`) for the affordance
  // inventory.
  useEffect(() => {
    if (!order || !onNavigateDeal) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (!readShortcutsEnabled(shortcutsPrefKey(cachedMeIdentity()))) return
      if (e.key === 'j' || e.key === 'J' || e.key === 'k' || e.key === 'K') {
        const key = e.key.toLowerCase()
        if (!shortcutEligible(e, key)) return
        // Slice 2: the pager steps over OPEN deals only, and is inert on a
        // won or lost deal — including one closed while it was open here.
        if (liveDeal.current && isClosedStage(liveDeal.current.stage)) return
        const pos = dealPosition(order, dealId)
        if (!pos) return
        const target = key === DEAL_NEXT_KEY ? pos.nextId : key === DEAL_PREV_KEY ? pos.prevId : null
        if (!target) return
        e.preventDefault()
        onNavigateDeal(target)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [order, onNavigateDeal, dealId])

  const missing = !dealQ.loading && !dealQ.error && !deal
  useEffect(() => { if (missing) onNotFound?.(dealId) }, [missing, dealId, onNotFound])

  if (dealQ.loading) return <DealDetailSkeleton />
  if (dealQ.error) return <ErrorState message={dealQ.error} />
  if (!deal) return <DealNotFound onBackToPipeline={onNavigate && (() => onNavigate('sales.pipeline'))} />

  // A write that returns the deal: `write` is the request's promise, created
  // just before this is called, so its ticket is the request's start. Every
  // such write also wrote history or a stage event: the timeline re-reads
  // both, silently — and the deal is re-read (§40.12).
  const applyWrite = async (write: Promise<DealDetail>): Promise<void> => {
    const t = ++ticket.current
    const id = dealId
    const d = await write
    applyAnswer(t, id, d)
    changes.refresh()
    reassess()
  }

  const activities = [
    ...added.filter(a => a.dealId === dealId),
    ...(actsQ.data ?? []),
  ].map(a => patched[a.id] ?? a)

  const tasks = activities.filter(a => a.activityType === 'task')

  const isClosed = deal.stage === 'Closed Won' || deal.stage === 'Closed Lost'
  const events = changes.events
  const now = Date.now()

  // Slice A: the company LINKS to the account 360 when the deal carries an
  // account_id; unlinked deals keep the plain text.
  const linkedAccount = deal.accountId
  const pill = priorityPill(deal.priority)
  const createdLabel = formatDay(deal.createdAt)

  // Slice 2 (§39.9 order 2; spec §3.4 C): ONE timeline, merged client-side
  // from the activities, the field history and the stage events.
  const timeline = buildTimeline({
    deal, activities: activities as WireActivity[], history: changes.history ?? [], events: events ?? [],
    joins: {
      employeeName: id => (employeesQ.data ?? []).find(x => x.id === id)?.fullName,
      accountName: id => (accountsQ.data ?? []).find(x => x.id === id)?.name,
      contactName: id => links?.items.find(i => i.contactId === id)?.name
        ?? (namesQ.data ?? []).find(n => n && n[0] === id)?.[1]
        ?? (contactQ.data && contactQ.data.id === id ? contactQ.data.name : undefined),
    },
  })

  // A move, and its Undo: the reverse move, offered for UNDO_MS on the toast.
  // Nothing is deleted — the events table is append-only, so an undo is a
  // second event (§39.6), and the undo's own toast offers no further undo.
  //
  // The guard reads the LIVE deal and in-flight flag through refs, never this
  // render's `deal` / `moving`: sonner holds the Undo callback for UNDO_MS,
  // long after the render that created it, and that render's `deal` still
  // carries the PRE-move stage — a guard on it read "already there" and
  // refused the reverse move (slice 1b, §38.53: found by the live proof).
  async function moveTo(stage: DealStage, from?: DealStage, undo = false) {
    const cur = liveDeal.current
    if (!cur || inFlight.current || isClosedStage(cur.stage) || stage === cur.stage || !OPEN_STAGES.includes(stage)) return
    const slug = STAGE_TO_SLUG[stage]
    if (!slug) return
    inFlight.current = true
    setMoving(true)
    try {
      await applyWrite(moveDealStage(dealId, slug).then(mapDealDetail))
      void refreshSalesFigures()
      if (from && OPEN_STAGES.includes(from)) {
        toast.success(`Moved to ${stage}`, { duration: UNDO_MS, action: { label: 'Undo', onClick: () => { void moveTo(from, undefined, true) } } })
      } else {
        toast.success(undo ? `Moved back to ${stage}` : `Moved to ${stage}`)
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to move stage'))
    } finally {
      inFlight.current = false
      setMoving(false)
    }
  }

  // R7 / Rider A: the EXISTING route, behind the role gate and a confirmation
  // that names the deal. The server's refusal shows as its own sentence.
  async function removeDeal() {
    if (deleting) return
    setDeleting(true)
    try {
      await deleteDeal(dealId)
      void refreshSalesFigures()
      toast.success(`Deleted ${deal!.title}`)
      onDeleted?.()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to delete the deal'))
    } finally {
      setDeleting(false)
    }
  }

  // Slice 4: a suggestion chip and the manual add both land here — the
  // existing activities POST, with the due day as noon London when there is one.
  async function addTask(subject: string, dueAt?: string): Promise<boolean> {
    try {
      const created = await createDealActivity({ dealId, activityType: 'task', subject, ...(dueAt ? { dueAt } : {}) })
      setAdded(prev => [created, ...prev])
      reassess()
      void refreshSalesFigures()
      return true
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to add task'))
      return false
    }
  }

  // Done is the STATUS (nextSteps.isTaskDone): the reopen route leaves
  // completed_at behind, so reading it kept a reopened task ticked for good.
  async function toggleTask(t: WireActivity) {
    const done = isTaskDone(t)
    try {
      const updated = done ? await reopenActivity(t.id) : await completeActivity(t.id)
      setPatched(prev => ({ ...prev, [t.id]: updated }))
      reassess()
      void refreshSalesFigures()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update task'))
    }
  }


  // R3 (§40.13): confirm or dismiss the OPEN episode behind a failing tile.
  // The server appends the act (201) or answers this session's identical
  // latest act (200); the page then RE-READS the deal, so the line it shows is the
  // server's answer, never one assembled here. One act in flight at a time.
  async function actOnFlag(rule: string, body: RiskFlagActionBody) {
    if (acting) return
    setActing(rule)
    try {
      await recordRiskFlagAction(dealId, rule, body)
      toast.success(body.action === 'dismiss' ? 'Risk flag dismissed' : 'Risk flag confirmed')
      reassess()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to record the decision on this flag'))
    } finally {
      setActing(null)
    }
  }

  // §40.12 (rule set 2): the card's checks, its score and status, and the
  // stage bar's age and threshold are the SERVER's (deal.assessment) — the
  // same evaluation the board badge renders. "Today" is the server's London
  // day it evaluated on; without an assessment (a closed deal, or a wire that
  // lacks it) the card is not drawn and nothing on the bar is red.
  const assessment = deal.assessment ?? null
  const todayIso = assessment?.today ?? londonDay(serverNow())
  const stageDays = assessment?.stage.days ?? deal.daysInStage
  const redAfterDays = assessment ? assessment.stage.thresholdDays : null
  const primaryName = links ? (links.items.find(i => i.isPrimary)?.name ?? null) : undefined
  const checks = assessment ? healthChecks({
    deal, assessment,
    contactName: deal.contactId && contactQ.data && contactQ.data.id === deal.contactId ? contactQ.data.name : null,
    linked: links ? { count: links.items.length, primaryName: primaryName ?? null } : null,
  }) : []
  const closeView = isClosed || !assessment ? null : closeDateCard({ deal, checks, assessment, todayIso })
  // A link write: the new list at once; the deal's primary mirror comes from
  // the re-read (the server's answer), never from a deal assembled here.
  const applyLinks = (next: SalesDealContacts) => {
    setLinksOverride({ id: dealId, links: next })
    changes.refresh()
    reassess()
  }
  const summary = summarySentence({ position: positionQ.data, assessment })
  const weighted = weightedLabel(deal)

  const companyNode = deal.company
    ? (linkedAccount && onOpenAccount ? (
        <button type="button" onClick={() => onOpenAccount(linkedAccount)} title="Open account"
          className="font-medium hover:underline" style={{ color: info.text }}>
          {deal.company}
        </button>
      ) : <span>{deal.company}</span>)
    : null
  const metaParts: React.ReactNode[] = []
  if (companyNode) metaParts.push(companyNode)
  if (deal.owner) metaParts.push(<span className="inline-flex items-center gap-1.5"><Avatar name={deal.owner} size={20} />{deal.owner}</span>)
  const sourced = sourcePhrase(deal.source)
  if (sourced) metaParts.push(<span>{sourced}</span>)
  if (createdLabel) metaParts.push(<span>Created {createdLabel}</span>)

  return (
    <div className="page-enter flex flex-col gap-5">
      {/* The openers with no crumb for the deal keep their Back link. */}
      {onBack && backLabel && (
        <button onClick={onBack}
          className="flex items-center gap-2 text-12 font-medium text-ink-secondary hover:text-ink-primary transition-colors self-start">
          <ArrowLeft size={14} /> {backLabel}
        </button>
      )}

      {/* Hero (spec §3.2) */}
      <div className="flex items-start justify-between gap-6 flex-wrap" data-deal-hero>
        <div className="flex items-start gap-4 min-w-0">
          <div className="w-14 h-14 rounded-[16px] flex items-center justify-center text-16 font-semibold text-ink-primary flex-shrink-0"
            style={{ background: surface.control }} aria-hidden="true">
            {initialsOf(deal.company || deal.title)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-[30px] font-bold text-ink-primary leading-tight" style={{ letterSpacing: '-0.022em' }}>{deal.title}</h1>
              {pill && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-12 font-medium" data-priority={deal.priority}
                  style={pill.tone === 'warning' ? { background: warning.bg, color: warning.text } : { background: surface.control, color: ink.secondary }}>
                  {pill.label}
                </span>
              )}
            </div>
            <p className="text-14 text-ink-muted mt-1 flex items-center gap-2 flex-wrap">
              {metaParts.map((part, i) => <span key={i} className="inline-flex items-center gap-2">{i > 0 && <span aria-hidden="true">·</span>}{part}</span>)}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {/* The value in short form (R3: the pinned rule, "£12.0M" kept); the
              exact figure is the tooltip. "Weighted £{x} · {n}%" to its left
              (spec §3.2) — since slice 8 the SERVER's figure (row 216:
              salesarith, fenced GBP, floored; the page never multiplies),
              "under £1" when it would print £0 on a deal with value (row 217),
              shown only when the server could weigh the deal. */}
          <div className="flex items-baseline gap-3">
            {weighted != null && deal.weightedPence != null && deal.effectiveProbability != null && (
              <span className="text-13 text-ink-muted" title={money(deal.weightedPence / 100, 'GBP')} data-deal-weighted>
                Weighted {weighted} · {deal.effectiveProbability}%
              </span>
            )}
            <div className="text-[34px] font-bold text-ink-primary leading-none" style={{ letterSpacing: '-0.025em' }}
              title={money(deal.amountPence / 100, deal.currency)} data-deal-value>
              {compactMoney(deal.amountPence, deal.currency)}
            </div>
          </div>
          {/* FX provenance (SB-6): individual figures only — the captured
              base and its fixing date; '' (GBP / pre-capture) renders nothing. */}
          {fxProvenance(deal.baseValuePence, deal.fxRateDate) !== '' && (
            <div className="text-11 text-ink-muted">{fxProvenance(deal.baseValuePence, deal.fxRateDate)}</div>
          )}
          <div className="flex items-center gap-2 mt-1">
            {records && <DealMenu dealTitle={deal.title} canDelete={canDelete} deleting={deleting} onEdit={() => setEditTitleSignal(n => n + 1)} onDelete={() => void removeDeal()} />}
            {/* Close actions (§5.E / F2) — open stages only; both land in the
                shared CloseDealModal with the outcome preset. Small on purpose:
                closing is a deliberate action (spec §3.2). */}
            {!isClosed && records && (
              <>
                <button type="button" className="k-btn-secondary" style={{ background: surface.card }} onClick={() => setClosing('lost')}>Mark lost</button>
                <button type="button" className="k-btn-secondary" style={{ background: surface.card, color: success.text }} onClick={() => setClosing('won')}>Mark won</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stage bar (spec §3.3); red past R6's threshold (slice 6). */}
      <StageBar stage={deal.stage} daysInStage={stageDays} events={events ?? null} now={now} moving={moving}
        thresholdDays={redAfterDays}
        onMove={to => { void moveTo(to, deal.stage) }} onMarkWon={() => setClosing('won')} />

      {/* Two-column body: Next steps / Timeline | People / Forecast / Details */}
      {/* Spec §3.1: a 2-column grid, minmax(0,1fr) 380px, 20px gap, top-aligned.
          Below 1100px the rail moves under the left column, People first. */}
      <div className="grid grid-cols-1 min-[1100px]:grid-cols-[minmax(0,1fr)_380px] gap-5 items-start" data-deal-grid>
        <div className="flex flex-col gap-5 min-w-0">
          {/* Next steps (spec §3.4 B, slice 4) — tasks are sales_activities of
              type 'task' (D3-1a; no deal_tasks table). Replaces the Tasks card. */}
          {/* ✦ Deal health (spec §3.4 A, slice 6) — the rules for an OPEN deal. */}
          {!isClosed && assessment && <DealHealthCard checks={checks} summary={summary} score={assessment.score} status={assessment.status}
            stageOver={assessment.stage.over} stageDays={assessment.stage.days}
            noDecisionMaker={(deal.health ?? []).some(s => s.code === 'no_decision_maker')}
            riskFlags={deal.riskFlags} riskFlagsOn={riskFlagsOn} canDecide={mayAct} acting={acting} onAct={(rule, body) => { void actOnFlag(rule, body) }} />}
          <NextStepsCard deal={deal} tasks={tasks} loading={actsQ.loading} failed={Boolean(actsQ.error)}
            contactName={primaryName !== undefined
              ? primaryName
              : (deal.contactId ? (contactQ.data && contactQ.data.id === deal.contactId ? contactQ.data.name : '') : null)}
            onCreate={addTask} onToggle={t => { void toggleTask(t) }} />

          {/* Timeline (spec §3.4 C) — the activity list and the change
              history are now ONE card; the separate audit strip is gone
              from this page (it stays on the account, contact and lead pages). */}
          <DealTimeline deal={deal} items={timeline}
            loading={actsQ.loading || changes.loading}
            activitiesFailed={Boolean(actsQ.error)}
            changesFailed={changes.historyError || changes.eventsError}
            onNoteCreated={created => { setAdded(prev => [created, ...prev]); reassess() }} />
        </div>

        <div className="flex flex-col gap-5 min-w-0" data-deal-rail>
          <DealPeopleCard deal={deal} links={links ?? null} loading={!links && linksQ.loading} failed={!links && Boolean(linksQ.error)} onLinksChanged={applyLinks}
            canSetRoles={canDelete || mayAct} />
          <CloseDateCard key={deal.id} deal={deal} view={closeView} onApply={applyWrite} />
          <ForecastCard deal={deal} history={changes.history ?? []} onApply={applyWrite} />
          <DetailsCard key={deal.id} deal={deal} history={changes.history ?? []} onApply={applyWrite}
            onOpenAccount={onOpenAccount} onNavigate={onNavigate} editTitleSignal={editTitleSignal} />
        </div>
      </div>

      {/* Close ceremony (§5.C/§5.E) — the response is the updated wire deal;
          mapping it through mapDealDetail refreshes stage, closedAt,
          lostReason, and (on a win) the invoice row in one apply. */}
      {closing && records && (
        <CloseDealModal
          deal={{ id: deal.id, title: deal.title, company: deal.company, amountPence: deal.amountPence, currency: deal.currency }}
          outcome={closing}
          onCancel={() => setClosing(null)}
          onClosed={wire => {
            // The modal blocks the page while its close is in flight, so no
            // other write starts meanwhile; the answer is ticketed here.
            void applyWrite(Promise.resolve(mapDealDetail(wire)))
            setClosing(null)
          }}
        />
      )}
    </div>
  )
}
